/**
 * Headline tiles for the network dashboard (plan §7.6). Accountable mode: every
 * value carries its source, its basis and the day it runs through; internal
 * figures wear the tag even here (§7.1). Placeholders exist by operator
 * ruling D11 — a gated source shows the card it will fill, naming the gate.
 */
import { Line, LineChart, ResponsiveContainer } from 'recharts';
import { Lock, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import type { MetricVisibility } from '../../lib/api/analytics.js';
import { compact, type Trend } from '../../lib/analyticsShape.js';

export function VisibilityTag({ visibility }: { visibility: MetricVisibility }) {
  return visibility === 'public_safe' ? (
    <span className="inline-flex items-center rounded-full border border-success-200 bg-success-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-success-700">
      public-safe
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full border border-grey-200 bg-grey-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-grey-600">
      internal
    </span>
  );
}

export function EstimatedTag({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span
      className="inline-flex items-center rounded-full border border-warning-200 bg-warning-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-warning-700"
      title="Cloudflare returned a sampled figure for these days (sample interval > 1.5). They are estimates, not measurements."
    >
      {count} estimated
    </span>
  );
}

const TREND_STYLE = {
  up: { Icon: TrendingUp, cls: 'text-success-600', word: 'up' },
  flat: { Icon: Minus, cls: 'text-info-600', word: 'stable' },
  down: { Icon: TrendingDown, cls: 'text-danger-600', word: 'down' },
} as const;

/** Up (green) / stable (blue) / down (red) against the previous window.
 *  Stable is a band (±5%, or √previous for small counts), not equality. */
export function TrendBadge({ trend }: { trend: Trend }) {
  const { Icon, cls, word } = TREND_STYLE[trend.direction];
  const pct = `${trend.pct >= 0 ? '+' : '−'}${Math.abs(trend.pct * 100).toFixed(Math.abs(trend.pct) < 0.1 ? 1 : 0)}%`;
  const title = `${word}: ${Math.round(trend.current).toLocaleString()} vs ${Math.round(trend.previous).toLocaleString()} in the ${trend.against} (${pct}). `
    + `Stable band ±${Math.round(trend.band).toLocaleString()} (the wider of ±5% and √previous).`;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${cls}`} title={title} aria-label={title}>
      <Icon className="h-5 w-5" aria-hidden />
      <span>{word === 'stable' ? 'stable' : pct}</span>
      <span className="font-normal text-grey-400">vs {trend.against}</span>
    </span>
  );
}

export interface StatTileProps {
  label: string;
  value: number | null | undefined;
  /** e.g. "rolling 30 days" · "last 7 days" · "point in time" */
  basis: string;
  visibility: MetricVisibility;
  /** e.g. "Cloudflare beacon · through 2026-09-14" */
  source: string;
  /** Per-site line under the value. */
  sub?: string;
  estimatedDays?: number;
  spark?: Array<{ date: string; v: number | null }>;
  /** vs the previous window of the same length; omitted when not comparable */
  trend?: Trend | null;
  /** Shown instead of a badge, saying why there is none. */
  trendNote?: string | null;
}

export function StatTile({ label, value, basis, visibility, source, sub, estimatedDays = 0, spark, trend, trendNote }: StatTileProps) {
  return (
    <div className="bg-surface rounded-xl border border-grey-200 p-4 flex flex-col gap-2 min-h-[148px]">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium text-grey-500">{label}</p>
        <VisibilityTag visibility={visibility} />
      </div>
      <div className="flex items-baseline gap-3">
        <p className="text-3xl font-semibold text-grey-900 leading-none">{compact(value)}</p>
        {trend ? <TrendBadge trend={trend} /> : trendNote ? <span className="text-[11px] text-grey-400">{trendNote}</span> : null}
      </div>
      {spark && spark.length > 1 && (
        <div className="h-7 w-full" aria-hidden>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={spark} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
              <Line type="monotone" dataKey="v" stroke="var(--viz-muted)" strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="mt-auto space-y-0.5">
        {sub && <p className="text-xs text-grey-500 truncate" title={sub}>{sub}</p>}
        <p className="text-[11px] text-grey-400 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-medium text-grey-500">{basis}</span>
          <span>{source}</span>
          <EstimatedTag count={estimatedDays} />
        </p>
      </div>
    </div>
  );
}

export function AwaitingSourceTile({ label, gate, basis, visibility }: { label: string; gate: string; basis: string; visibility: MetricVisibility }) {
  return (
    <div className="rounded-xl border border-dashed border-grey-300 bg-grey-25 p-4 flex flex-col gap-2 min-h-[148px]">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium text-grey-500">{label}</p>
        <VisibilityTag visibility={visibility} />
      </div>
      <p className="text-3xl font-semibold text-grey-300 leading-none">—</p>
      <div className="mt-auto space-y-0.5">
        <p className="text-xs text-grey-500 flex items-center gap-1"><Lock className="h-3 w-3" /> Awaiting source</p>
        <p className="text-[11px] text-grey-400"><span className="font-medium text-grey-500">{basis}</span> · {gate}</p>
      </div>
    </div>
  );
}
